import DriverContactButtons from '@/customComponents/sharedComponents/DriverContactButtons.jsx'
import {isDriverRevealed} from '@/utils/functions'

/**
 * WHO IS COMING — photo, name, car, plate, and the two ways to reach them.
 *
 * A compact sibling of the driver block in `RideView/RideDetails.jsx`, not a second source
 * of truth: it reads the same `trip.driver` object and defers to the same
 * `isDriverRevealed` gate. The details page has a whole card to spend on ratings,
 * credentials and a verified badge; this one sits under a map on a phone and has room for
 * the four facts that identify a car pulling up.
 *
 * ── ⚠️ THE REVEAL GATE IS NOT DECORATION ──────────────────────────────────────────
 * `isDriverRevealed` allows a driver's identity only from `EnRoute` onwards, and its own
 * comment says why: that is the moment they commit to this ride. This component is only
 * ever rendered on the approaching and riding screens, so the gate should always pass —
 * it is checked anyway, because a status arriving from a socket a fraction before the trip
 * refetches is exactly the kind of window where "should always" stops being true, and the
 * failure mode is naming a driver to the wrong passenger.
 */
export default function DriverCard({trip}) {
    const driver = trip?.driver
    if (!driver || !isDriverRevealed(trip)) return null

    const vehicle = driver.vehicle
    const car = [vehicle?.make, vehicle?.model].filter(Boolean).join(' ')

    return (
        <section className="white-global-section flex items-center gap-4 max-md:gap-3 max-md:p-3! max-md:shadow-none! max-md:rounded-2xl! max-md:border max-md:border-(--border-color)">
            {driver.photo
                ? <img src={driver.photo} alt="" className="h-14 w-14 shrink-0 rounded-full bg-(--bg-grey) object-cover max-md:h-10 max-md:w-10"/>
                : <span className="h-14 w-14 shrink-0 rounded-full bg-(--bg-grey) max-md:h-10 max-md:w-10"/>}

            <div className="min-w-0 flex-1">
                <p className="mb-0! truncate font-bold max-md:whitespace-normal max-md:break-words max-md:text-sm">{driver.name}</p>
                <p className="mb-0! text-sm text-(--text-secondary) max-md:text-xs">Your Driver</p>

                {(car || vehicle?.plate_no) && (
                    <p className="mb-0! mt-2 flex flex-wrap items-center gap-2 text-sm max-md:mt-1 max-md:gap-1.5 max-md:text-xs">
                        {car && <span className="font-medium text-(--text-primary)">{car}</span>}
                        {/* The plate is the one thing that tells two identical silver
                            saloons apart at a kerb, so it gets a chip of its own rather
                            than being run into the model name. */}
                        {vehicle?.plate_no && (
                            <span className="rounded-full bg-(--accent-green-light) px-3 py-1 font-semibold text-(--text-primary) max-md:px-2 max-md:py-0.5">
                                {vehicle.plate_no}
                            </span>
                        )}
                    </p>
                )}
            </div>

            <DriverContactButtons trip={trip}/>
        </section>
    )
}

export {DriverCard}
